import React from 'react';
import styled from 'styled-components';
import {
  PhoneOutlined,
  EnvironmentOutlined,
  MailOutlined,
  TagOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { theme } from '../../styles/theme';
import { ScrollReveal } from '../animations/ScrollReveal';

const FooterContainer = styled.footer`
  background: linear-gradient(to bottom, #001529, #002140);
  color: ${theme.colors.white};
  padding: 80px 0 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(255,255,255,0) 0%,
      rgba(255,255,255,0.3) 50%,
      rgba(255,255,255,0) 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%);
    pointer-events: none;
  }
`;

const FooterWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 60px;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 50px;
    text-align: center;
  }
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 25px;
  color: ${theme.colors.white};
  position: relative;
  padding-bottom: 12px;
  font-weight: 600;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${theme.colors.primary};
    transition: width 0.3s ease;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  ${FooterSection}:hover &::after {
    width: 60px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: center;
  }
`;

const LogoImage = styled.img`
  width: 100px;
  height: auto;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  filter: brightness(1.1);

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.2);
  }
`;

const LogoDescription = styled.p`
  font-size: 14px;
  line-height: 1.8;
  margin: 0;
  max-width: 400px;
  color: rgba(255, 255, 255, 0.8);

  @media (max-width: ${theme.breakpoints.md}) {
    text-align: center;
  }
`;

const ContactInfo = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    color: ${theme.colors.primary};
    transform: translateX(5px);
  }

  svg {
    font-size: 18px;
    color: ${theme.colors.primary};
    flex-shrink: 0;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    justify-content: center;
    
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const QuickLinks = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const QuickLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '→';
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s ease;
  }

  &:hover {
    color: ${theme.colors.primary};
    
    &::before {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: ${theme.breakpoints.md}) {
    justify-content: center;
  }
`;

// const SocialLinks = styled.div`
//   display: flex;
//   gap: 20px;
//   margin-top: 25px;

//   @media (max-width: ${theme.breakpoints.md}) {
//     justify-content: center;
//   }
// `;

// const SocialIcon = styled.a`
//   color: rgba(255, 255, 255, 0.8);
//   font-size: 24px;
//   transition: all 0.3s ease;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 40px;
//   height: 40px;
//   border-radius: 50%;
//   background: rgba(255, 255, 255, 0.1);

//   &:hover {
//     color: ${theme.colors.primary};
//     background: rgba(255, 255, 255, 0.15);
//     transform: translateY(-3px);
//   }
// `;

const Copyright = styled.div`
  text-align: center;
  margin-top: 60px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
`;

export const Footer: React.FC = () => {
  return (
    <FooterContainer>
      <ScrollReveal>
        <FooterWrapper>
          <LogoSection>
            <LogoImage src="./logo.png" alt="XLvolution Logo" />
            <LogoDescription>
              XLvolution致力于为企业提供创新的技术解决方案，助力企业数字化转型。我们专注于智能化系统开发，为客户创造更大的价值。
            </LogoDescription>
          </LogoSection>

          <FooterSection>
            <FooterTitle>快速使用</FooterTitle>
            <QuickLinks>
              {/* <QuickLink href="https://xlvolution.cn " target="_blank">
                H版---管理系统
              </QuickLink> */}
              {/* <QuickLink href="#" target="_blank">
                外呼系统
              </QuickLink> */}
              <QuickLink href="https://i.xlvolution.cn" target="_blank">
                <CommentOutlined
                  style={{ color: theme.colors.primary, fontSize: "18px" }}
                />
                聚合管理系统
              </QuickLink>
              <QuickLink href="https://t.xlvolution.cn" target="_blank">
                <TagOutlined
                  style={{ color: theme.colors.primary, fontSize: "18px" }}
                />
                投诉管理系统
              </QuickLink>
            </QuickLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>联系方式</FooterTitle>
            <ContactInfo href="tel:17608481999">
              <PhoneOutlined /> 17608481999
            </ContactInfo>
            <ContactInfo href="mailto:tt0824@139.com.com">
              <MailOutlined /> tt0824@139.com
            </ContactInfo>
            <ContactInfo href="https://maps.google.com" target="_blank">
              <EnvironmentOutlined />
              长沙市雨花区万家丽中路三段36号喜盈门国际大厦25楼25011
            </ContactInfo>
          </FooterSection>
        </FooterWrapper>
      </ScrollReveal>

      <Copyright>
        © {new Date().getFullYear()} XLvolution 湘ICP备2022026491号-2
      </Copyright>
    </FooterContainer>
  );
}; 